import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Fetchable } from '../../../store/appState';
import { RootState } from '../../../store/rootState';
import styled from 'styled-components/macro';
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import { useHistory, useParams } from 'react-router';
import { useTicketFromDescendantComment } from '../../../hooks/posts';
import { stringFromErrorOrAny } from '../../../utils/tools';
import { AppPaths } from '../../../constants/urls';
import { App, SignInState } from '../../../app';
import { RootPost } from '../../../RootPost';

interface PostDetailRewriteViewUrlParams {
    commentId: string;
}

export interface PostDetailsRewriteViewProps {
}

/**
 * Used to rewrite comments URL, sometimes sent from AH in notification emails:
 * /comments/1371473/view.html
 *   will be rewritten in PostDetailsRewriteView by calculating :ticketId to:
 *       -> /t/post-comment/:ticketId#T-ND1371473
 * @param props props
 * @returns React element
 */
export default function PostDetailsRewriteView(props: PostDetailsRewriteViewProps): JSX.Element {
    const { commentId: commentIdParam } = useParams<PostDetailRewriteViewUrlParams>();
    const commentId: number = Number.parseInt(commentIdParam);

    // load commentId, then rewrite history when loaded
    const signinState = useSelector((state: RootState) => state.appState.signInState);
    const ticket: Fetchable<RootPost> = useTicketFromDescendantComment(commentId);
    const state = useSelector((state: RootState) => state.appState);
    const history = useHistory();

    const answerHubId = useMemo(() => ticket?.data?.id, [ticket]);
    const title = useMemo(() => ticket?.data?.title, [ticket]);

    useEffect(() => {
        if (answerHubId == null) {
            return;
        }

        history.replace(`${AppPaths.t}/${App.GetUrlSafeTitle(title)}/${answerHubId}#T-ND${commentId}`);
    }, [answerHubId, title]);

    const authenticating = signinState === SignInState.Checking || signinState === SignInState.Executing;

    return <Wrapper data-cy="rap-post-details-view">
        <PageContent>
            <>
                {(authenticating || ticket?.isFetching) && <Fetching>
                    <Spinner size={SpinnerSize.large} />
                    <div>Fetching feedback item ...</div>
                </Fetching>}

                {!authenticating && !ticket?.isFetching && ticket?.hasErrors && <div css="padding: 2rem var(--page-margin-horizontal)">
                    <div>{"We were unable to get this feedback item. It could be because you don't have access to it or it doesn't exist"}</div>
                    <br />
                    {state.signInState !== SignInState.Success && <><div>Please try again after signing in.</div><br /></>}
                    <div>Error details: {stringFromErrorOrAny(ticket?.errors)}</div>
                </div>}
            </>
        </PageContent>
    </Wrapper >
}

const Wrapper = styled.div`
    background-color: var(--color-background);
`;

// @TODO: Consolidate some common styling in PageContent on different pages
const PageContent = styled.main`
    min-height: 60vh;
    margin-left: auto;
    margin-right: auto;
    padding: 1.5rem 0;

    >* + * {
            margin-top: 2rem;
    }

    .ms-Dropdown-container {
        display: inline-block;
        min-width: 15rem;
        margin-top: .5rem;
    }

    .ms-Dropdown-title {
            border-color: var(--color-surface-dark1);
    }

    .title-instruction {
        font-size: 1.8rem;
        line-height: 2.4rem;
        font-weight: 350;

        .btn-link {
            padding: 0;
            height: auto;
        }
    }

    .selected-post-header.collapsed {
        background-color: var(--color-secondary);
    }

    .selected-post-header {
        font-size: 2.4rem;
    }

    .toolbar {
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    .ms-MessageBar {
        padding: .2rem 0;

        span {
            font-size: 1.6rem;
        }

        .ms-MessageBar-icon {
            font-size: 1.8rem;
        }
    }

    .accordion-content {
        border: none;
    }
`;

const Fetching = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: 10rem;

    > * + * {
        margin-top: 3rem;
    }
`;